import React, { FC, useEffect, useState } from 'react'
import { Tooltip, VStack, Slider, SliderMark, SliderTrack, SliderFilledTrack, SliderThumb } from '@chakra-ui/react'
import { Button } from '..'
import { useTranslation } from 'react-i18next'
import { ScenarioStep } from '../../state/interfaces'

export interface SliderDataProps {
  onChange?: (newValues: string[]) => void
  onConfirm?: (values: string[]) => void
  name: string
  metadata?: ScenarioStep['metadata']
}

export const SliderData: FC<SliderDataProps> = ({ onChange, name, metadata }) => {
  const { t } = useTranslation()
  const [sliderValue, setSliderValue] = useState(25)
  const [showTooltip, setShowTooltip] = useState(false)
  const [sliderData, setSliderData] = useState(metadata)

  useEffect(() => {
    if (sliderValue > 150) {
      setSliderData({
        ...metadata,
        max: 900,
        min: 0,
        sliderMark: [
          {
            value: 0,
            text: '0',
          },
          {
            value: 150,
            text: '150',
          },
          {
            value: 400,
            text: '400',
          },
          {
            value: 900,
            text: '900',
          },
        ],
        step: 100,
        value: sliderValue,
      })
    } else if (sliderValue < 200) {
      setSliderData({
        ...metadata,
        step: sliderValue > 50 ? 10 : 5,
      })
    }
  }, [sliderValue])

  const dataUnitText =
    sliderData!.dataUnitType === 'GB' ? t('common_GB') : sliderData!.dataUnitType === 'Mbps' ? t('common_mbps') : ''

  return (
    <VStack width={{ base: '90%', md: 'full' }} maxWidth={500} marginTop={5} paddingX={{ base: 6, md: 0 }}>
      <Slider
        id="slider"
        defaultValue={sliderData!.defaultValue as number}
        min={sliderData!.min as number}
        name={name}
        max={sliderData!.max as number}
        height={100}
        value={sliderData!.value ? (sliderData!.value as number) : sliderValue}
        step={sliderData!.step as number}
        colorScheme={sliderData!.colorScheme as string}
        onChange={(e) => setSliderValue(e)}
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
        onTouchStart={() => setShowTooltip(true)}
        onTouchEnd={() => setShowTooltip(false)}
      >
        {(sliderData!.sliderMark as { value: number; text: string }[]).map((sliderMark, index, array) => {
          const isFirst = index === 0
          const isLast = index === array.length - 1
          let marginLeft = '-2.5'
          if (isFirst) marginLeft = '0'
          if (isLast) marginLeft = '-9'

          return (
            <SliderMark
              key={`${sliderMark.value}-${sliderMark.text}`}
              value={sliderMark.value}
              mt="1"
              ml={marginLeft}
              fontSize="xs"
              color="darkGrey"
            >
              {sliderMark.text}
              {!isFirst && dataUnitText}
            </SliderMark>
          )
        })}
        <SliderTrack backgroundColor="grey" height={3} borderRadius="full">
          <SliderFilledTrack backgroundColor="green.500" />
        </SliderTrack>
        <Tooltip
          hasArrow
          bg="green.500"
          color="white"
          placement="top"
          fontSize="xs"
          fontWeight="bold"
          isOpen={showTooltip}
          label={`${sliderValue}${dataUnitText}`}
        >
          <SliderThumb boxSize={5} />
        </Tooltip>
      </Slider>
      <Button
        onClick={() => {
          if (dataUnitText === t('common_GB')) {
            onChange && onChange([(sliderValue * 1000).toString() + 'MB'])
          } else {
            onChange && onChange([`${sliderValue.toString()}`])
          }
        }}
        variant="solid"
        // color="purple"
        backgroundColor="purpleAlpha"
        fontSize="xs"
        fontWeight="bold"
        marginTop={40}
      >
        {t('common_confirm')}
      </Button>
    </VStack>
  )
}
